// TurnstileDisplay.jsx
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { withTranslation } from 'react-i18next';

import { renderTurnstileWidget } from '../resource/turnstile.js';
import { SET_OPERATION_DATA } from '../ActionTypes.js';
import {
  CLOUDFLARE_TURNSTILE_SITE_KEY,
  CAPTCHA_VERIFICATION_TIMEOUT_SEC,
} from '../RemoteConfigKeys.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getIsCaptchaVerified from '../selector/getIsCaptchaVerified.js';
import pushToastr from '../action/pushToastr.js';
import env from '../resource/env.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { captcha } from '../resource/debug.js';

const log = captcha.extend('log:TurnstileDisplay');
const errorLog = captcha.extend('error:TurnstileDisplay');
/**
 * Turnstile verification component
 * Automatically saves token to Redux store (operation.turnstile.token) on success
 * @kind component
 * @param {Object} props - Component props
 * @param {boolean} props.showErrorText - Whether to show verification error message
 * @param {Function} props.t - Translation function
 * @return {React.ReactNode} Turnstile widget container
 */
const TurnstileDisplay = ({ showErrorText = false, t }) => {
  const dispatch = useDispatch();

  const isCaptchaVerified = useSelector(state => getIsCaptchaVerified(state));

  const sitekey = useSelector(state =>
    getRemoteConfigData(state, CLOUDFLARE_TURNSTILE_SITE_KEY)
  );
  const verificationTimeoutSeconds =
    useSelector(state =>
      getRemoteConfigData(state, CAPTCHA_VERIFICATION_TIMEOUT_SEC)
    ) || env.CAPTCHA_VERIFICATION_TIMEOUT_SEC;

  const widgetIdRef = useRef(null);
  const verificationTimeoutRef = useRef(null);
  const containerId = useRef(`turnstile-display-${uuidv4()}`).current;

  useEffect(() => {
    if (widgetIdRef.current) return;
    if (!sitekey) return;

    const clearVerificationTimeout = () => {
      if (verificationTimeoutRef.current) {
        clearTimeout(verificationTimeoutRef.current);
        verificationTimeoutRef.current = null;
        log('[TurnstileDisplay] verification timeout cleared');
      }
    };

    const handleSuccess = token => {
      clearVerificationTimeout();
      log('[TurnstileDisplay] verification success');
      dispatch({
        type: SET_OPERATION_DATA,
        payload: {
          selectPath: ['turnstile'],
          data: { token },
        },
      });
    };

    const handleError = error => {
      clearVerificationTimeout();
      errorLog('[TurnstileDisplay] error', error);
    };

    const handleExpired = () => {
      clearVerificationTimeout();
      log('[TurnstileDisplay] expired');
    };

    const handleTimeout = () => {
      clearVerificationTimeout();
      log('[TurnstileDisplay] official timeout');
    };

    const renderWidget = async () => {
      // Start timeout timer immediately (covers both widget load and verification)
      verificationTimeoutRef.current = setTimeout(() => {
        log(
          '[TurnstileDisplay] widget load/verification timeout - showing toast'
        );
        dispatch(
          pushToastr({
            textKey: 'kyc_failure_general',
            color: 'error',
            durationSec: 10,
          })
        );
      }, verificationTimeoutSeconds * 1000);
      log('[TurnstileDisplay] timeout timer started', {
        verificationTimeoutSeconds,
      });

      try {
        const widgetId = await renderTurnstileWidget({
          containerId,
          sitekey,
          onSuccess: handleSuccess,
          onError: handleError,
          onExpired: handleExpired,
          onTimeout: handleTimeout,
        });

        widgetIdRef.current = widgetId;
        log('[TurnstileDisplay] widget loaded successfully');
      } catch (error) {
        clearVerificationTimeout();
        errorLog('[TurnstileDisplay] render failed', error);
        dispatch(
          pushToastr({
            textKey: 'kyc_failure_general',
            color: 'error',
            durationSec: 10,
          })
        );
      }
    };

    renderWidget();

    return () => {
      clearVerificationTimeout();

      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        } catch (error) {
          errorLog('[TurnstileDisplay] cleanup error', error);
        }
      }

      dispatch({
        type: SET_OPERATION_DATA,
        payload: {
          selectPath: ['turnstile'],
          data: { token: null },
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey, verificationTimeoutSeconds]);

  if (!sitekey) {
    return null;
  }

  return (
    <StyledTurnstileDisplay>
      <TurnstileWidget id={containerId}></TurnstileWidget>
      {showErrorText && !isCaptchaVerified && (
        <ErrorNotice>
          {t('error_captcha', {
            ns: TranslationNamespace.AUTH,
          })}
        </ErrorNotice>
      )}
    </StyledTurnstileDisplay>
  );
};

const StyledTurnstileDisplay = styled.div`
  margin: 16px 0;
`;

const TurnstileWidget = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 65px;
  pointer-events: auto;
  position: relative;
  z-index: 100;

  > * {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
    align-self: stretch;
  }

  ${media.mobile`
    > * {
      width: auto;
      max-width: none;
      flex: 0 0 auto;
      align-self: auto;
      margin: 0 auto;
    }
  `}
`;

const ErrorNotice = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: ${color.red};
  margin-top: 4px;

  ${media.mobile`
    font-size: 14px;
  `};
`;

TurnstileDisplay.propTypes = {
  showErrorText: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export { TurnstileDisplay };
export default withTranslation()(TurnstileDisplay);
